/**
 * Nested Yup schemas for agreement form contacts / price / discount rows (§6 + §5).
 * Field names use Formik array paths that match flattenFormikErrorEntries:
 *   contacts[0].contact_id, price_lines[1].title, …
 */

import * as Yup from "yup";

export function buildAgreementContactsSchema(t) {
  return Yup.array().of(
    Yup.object({
      _destroy: Yup.boolean(),
      party: Yup.string().oneOf(["customer", "supplier"]),
      contact_id: Yup.mixed().when(["_destroy", "party"], {
        is: (destroy, party) => !destroy && party === "customer",
        then: (s) =>
          s
            .nullable()
            .required(t("service.agreements.contacts.contact_required")),
        otherwise: (s) => s.nullable(),
      }),
      user_id: Yup.mixed().when(["_destroy", "party"], {
        is: (destroy, party) => !destroy && party === "supplier",
        then: (s) =>
          s
            .nullable()
            .required(t("service.agreements.contacts.employee_required")),
        otherwise: (s) => s.nullable(),
      }),
      role: Yup.string().nullable(),
    })
  );
}

export function buildAgreementPriceLinesSchema(t) {
  return Yup.array().of(
    Yup.object({
      _destroy: Yup.boolean(),
      title: Yup.string().when(["_destroy", "amount", "product_id"], {
        is: (destroy, amount, productId) =>
          !destroy &&
          (String(amount ?? "").trim() !== "" || productId != null),
        then: (s) =>
          s.trim().required(t("service.agreements.price_lines.title_required")),
        otherwise: (s) => s.nullable(),
      }),
      amount: Yup.number()
        .transform((value, original) =>
          original === "" || original == null ? null : value
        )
        .nullable()
        .typeError(t("service.agreements.price_lines.amount_invalid")),
      quantity: Yup.number()
        .transform((value, original) =>
          original === "" || original == null ? null : value
        )
        .nullable()
        .typeError(t("service.agreements.price_lines.quantity_invalid")),
      description: Yup.string().nullable(),
      interval_id: Yup.mixed().nullable(),
      product_id: Yup.mixed().nullable(),
    })
  );
}

export function buildAgreementDiscountLinesSchema(t) {
  return Yup.array().of(
    Yup.object({
      _destroy: Yup.boolean(),
      kind: Yup.string().oneOf(["manual", "category", "product"]),
      label: Yup.string().when(["_destroy", "kind"], {
        is: (destroy, kind) => !destroy && kind === "manual",
        then: (s) =>
          s.trim().required(t("service.agreements.discount_lines.label_required")),
        otherwise: (s) => s.nullable(),
      }),
      product_category_id: Yup.mixed().when(["_destroy", "kind"], {
        is: (destroy, kind) => !destroy && kind === "category",
        then: (s) =>
          s
            .nullable()
            .required(t("service.agreements.discount_lines.category_required")),
        otherwise: (s) => s.nullable(),
      }),
      product_id: Yup.mixed().when(["_destroy", "kind"], {
        is: (destroy, kind) => !destroy && kind === "product",
        then: (s) =>
          s
            .nullable()
            .required(t("service.agreements.discount_lines.product_required")),
        otherwise: (s) => s.nullable(),
      }),
      discount_percent: Yup.number()
        .transform((value, original) =>
          original === "" || original == null ? null : value
        )
        .nullable()
        .typeError(t("service.agreements.discount_lines.percent_invalid"))
        .when(["_destroy"], {
          is: (destroy) => !destroy,
          then: (s) =>
            s.required(t("service.agreements.discount_lines.percent_required")),
          otherwise: (s) => s.nullable(),
        }),
    })
  );
}

/** Nested formik error for a row field after submit. */
export function nestedRowFieldError(formik, arrayName, index, field) {
  if (!formik?.submitCount) return null;
  const rowErrors = formik.errors?.[arrayName]?.[index];
  if (!rowErrors || typeof rowErrors !== "object") return null;
  const msg = rowErrors[field];
  return typeof msg === "string" ? msg : null;
}

export function nestedFieldName(arrayName, index, field) {
  return `${arrayName}[${index}].${field}`;
}
